import { useContext } from 'react'

import {
  Button, Form, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import type { SidebarModalProps } from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import usePublicationEdition, { usePublicationEditionForm } from '@hooks/usePublicationEdition'

import formatDate, { formatToDateTime } from '@functions/formatDate'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { PublicationEditionModel } from '@models/types'

interface ModalCallbacks {
  closeModal: () => void,
  createPublicationEdition: (payload: HandleCallbackActionParams) => Promise<object>,
  updatePublicationEdition: (payload: HandleCallbackActionParams) => Promise<object>,
}

type CreateOrUpdatePublicationEditionParams = {
  entityState: Partial<PublicationEditionModel>,
  callbacks: ModalCallbacks,
  createFn: () => Promise<object>,
  updateFn: () => Promise<object>,
}

const createOrUpdatePublicationEdition = (params: CreateOrUpdatePublicationEditionParams) => {
  const {
    entityState, callbacks, createFn, updateFn,
  } = params
  const { closeModal, createPublicationEdition, updatePublicationEdition } = callbacks

  const actionType = entityState.id ? updateFn : createFn
  const actionText = entityState.id ? 'Updated' : 'Created'

  const publicationPayload = {
    callbacks: {
      action: actionType,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Edition ${actionText}`,
  }

  // @ts-expect-error TS2345
  if (entityState.id) return updatePublicationEdition(publicationPayload)

  // @ts-expect-error TS2345
  return createPublicationEdition(publicationPayload)
}

// @ts-expect-error TS7006
const changePublicationDate = (date, setEntityState, isEdit) => {
  if (isEdit){
    setEntityState({ publication_date: date, title: formatDate(date, 'yyyy-MM-dd', 'ccc LLL dd') })
    return
  }

  setEntityState({
    title: formatDate(date, 'yyyy-MM-dd', 'ccc LLL dd'),
    publication_date: date,
    booking_deadline: formatToDateTime(date, 'yyyy-MM-dd'),
    artwork_deadline: formatToDateTime(date, 'yyyy-MM-dd'),
  })
}

const CreateOrEditPublicationEditionModal = (props: SidebarModalProps & ModalCallbacks) => {
  const { callbacks, modalKey = 'CreateOrEditPublicationEditionModal', showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { publicationEdition } = modalPayload

  const {
    callbacks: {
      createPublicationEdition: createFn,
      updatePublicationEdition: updateFn,
    },
    creating,
    updating,
  } = usePublicationEdition(publicationEdition)

  const {
    entityState,
    entityState: {
      artwork_deadline,
      booking_deadline,
      editorial_deadline,
      inspection_deadline,
      id,
      publication_date,
      special_deadline,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = usePublicationEditionForm(publicationEdition)

  const saveOrCreateButtonText = id ? 'Update Publication Edition' : 'Create Publication Edition'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Create'} titleSecondLine="Edition" />

      <SidebarModal.Content>
        <Form>
          <Form.Field
            direction="column"
            errorMessage={errors.publication_date}
            label="* Publication Date"
          >
            <input
              name="publication_date"
              onChange={e => changePublicationDate(e.target.value, setEntityState, !!id)}
              type="date"
              value={publication_date}
            />
          </Form.Field>

          <Form.Field direction="column" errorMessage={errors.title} label="* Title" marginTop="large">
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <SectionDivider boxProps={{ marginY: 'large' }}>Booking Deadline</SectionDivider>

          <Form.Field direction="column" errorMessage={errors.booking_deadline} label="* Date">
            <input name="booking_deadline" type="datetime-local" value={booking_deadline} {...handlers} />
          </Form.Field>

          <SectionDivider boxProps={{ marginY: 'large' }}>Artwork Deadline</SectionDivider>

          <Form.Field direction="column" errorMessage={errors.artwork_deadline} label="* Date">
            <input name="artwork_deadline" type="datetime-local" value={artwork_deadline} {...handlers} />
          </Form.Field>

          <SectionDivider boxProps={{ marginY: 'large' }}>Editorial Deadline</SectionDivider>

          <Form.Field direction="column" label="Date">
            <input name="editorial_deadline" type="datetime-local" value={editorial_deadline} {...handlers} />
          </Form.Field>

          <SectionDivider boxProps={{ marginY: 'large' }}>Inspection Deadline</SectionDivider>

          <Form.Field direction="column" label="Date">
            <input name="inspection_deadline" type="datetime-local" value={inspection_deadline} {...handlers} />
          </Form.Field>

          <SectionDivider boxProps={{ marginY: 'large' }}>Special Deadline</SectionDivider>

          <Form.Field direction="column" label="Special Deadline">
            {/*
             // @ts-expect-error TS2322 */}
            <select data-value-type="boolean" name="special_deadline" value={special_deadline} {...handlers}>
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdatePublicationEdition({
            // @ts-expect-error TS2739
            entityState, callbacks, createFn, updateFn,
          })}
          size="large"
        >
          {saveEnabled ? saveOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

// @ts-expect-error TS7006
const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditPublicationEditionModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
